import { logger } from "@/util/logger";
import { setupConsolePIIFilter } from "@/util/mask-pii";
import { checkAndRescueIDB } from "@/helper/indexedDBHelper";
setupConsolePIIFilter();

import { useEffect, useRef, useState, Suspense } from "react";
import type { AppProps } from "next/app";
import Head from "next/head";
import Router from "next/router";
import { ToastContainer } from "react-toastify";
import { getMessaging, onMessage, isSupported } from "firebase/messaging";
import { FiChevronDown, FiChevronUp, FiLogOut } from "react-icons/fi";

import { initPostHog, trackPageView, identifyUser } from "@/util/analytics";
import { firebaseApp } from "@/util/firebase/firebase-config";
import { getAppMeta, isIOS } from "@/helper/helper";
import { SCORM_STORAGE_KEYS } from "@/Components/ScormResult/scormResultMessage";
import { markFeatureNamesHydrated } from "@/helper/featureNameHelper";
import { isSuspiciousEnvironment } from "@/util/detectSuspicious";
import {
  clearChunkLoadReloadFlag,
  isChunkLoadError,
  recoverFromChunkLoadError,
} from "@/util/chunkLoadRecovery";
import AvatarModelContextProvider from "@/contexts/AvatarModelContext";
import { protectedAxiosInstance } from "@/services/axios/axios";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { QueryClientProvider } from "@tanstack/react-query";
import { createQueryClient } from "@/util/queryClient";
import { AuthStoreProvider } from "@/contexts/AuthContext";
import { WebViewBridgeProvider } from "@/contexts/WebViewBridgeContext";
import ProfileLandmarkGate from "@/Components/ProfileLandmarkGate";

import Custom404 from "./404";
import ErrorBoundary from "@/Components/errorBoundary/ErrorBoundary";
import OfflineScreen from "./offlineScreen";
import UpdateAppModal from "@/Components/UpdateAppModal/UpdateAppModal";
import NotificationPermissionModal from "@/Components/NotificationPermissionModal/NotificationPermissionModal";
import OnboardingTour from "@/Components/OnboardingTour";
import Loader from "@/Components/Loader";
import PostHogController from "./posthog";

import "../styles/globals.css";
import "react-toastify/dist/ReactToastify.css";
import "../src/i18n";

// Polyfill for URL.parse for older browsers (e.g. Safari < 17.5) which is required by newer pdfjs-dist
if (typeof URL !== "undefined" && typeof (URL as any).parse !== "function") {
  (URL as any).parse = function (url: string | URL, base?: string | URL) {
    try {
      return new URL(url, base);
    } catch (e) {
      logger.log("Error parsing URL", e);
      return null;
    }
  };
}

interface UserData {
  userId: string;
  orgName: string;
}

// Content marked with data-allow-copy (the document module's PDF viewer) is
// exempt from the app-wide right-click and text-selection blocking.
const getEventElement = (target: EventTarget | null) =>
  target instanceof Element
    ? target
    : target instanceof Node
      ? target.parentElement // selectstart usually fires on a Text node
      : null;

const isCopyAllowed = (target: EventTarget | null) =>
  Boolean(getEventElement(target)?.closest("[data-allow-copy]"));

export function App({ Component, pageProps }: AppProps) {
  const [queryClient] = useState(createQueryClient);
  // After hydration, enable org custom feature names (read from localStorage)
  // and re-render once so pages pick them up. See helper/featureNameHelper.ts.
  const [, setFeatureNamesReady] = useState(false);
  useEffect(() => {
    markFeatureNamesHydrated();
    setFeatureNamesReady(true);
  }, []);
  const [userDetails, setUserDetails] = useState<UserData | null>(null);
  const [isSuspicious, setIsSuspicious] = useState(false);
  const [debouncedOnline, setDebouncedOnline] = useState<boolean>(true);
  const [pageTitle, setPageTitle] = useState("AI Trainings");
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [branding, setBranding] = useState<
    "mple" | "stratagile" | "synergetics" | null
  >(null);
  const [impersonationUserName, setImpersonationUserName] = useState("");
  const [isImpersonationBannerExpanded, setIsImpersonationBannerExpanded] =
    useState(false);

  useEffect(() => {
    const loadAdminImpersonation = () =>
      setImpersonationUserName(
        sessionStorage.getItem("isAdminImpersonation") === "true"
          ? sessionStorage.getItem("impersonatedUserName") || "this user"
          : ""
      );

    loadAdminImpersonation();
    window.addEventListener(
      "admin-impersonation-ready",
      loadAdminImpersonation
    );
    return () =>
      window.removeEventListener(
        "admin-impersonation-ready",
        loadAdminImpersonation
      );
  }, []);

  const exitImpersonation = () => {
    sessionStorage.removeItem("isAdminImpersonation");
    sessionStorage.removeItem("impersonatedUserName");
    setImpersonationUserName("");
    setIsImpersonationBannerExpanded(false);
    window.close();
    window.setTimeout(() => {
      localStorage.clear();
      window.location.assign("/signin?resetTheme=1");
    }, 100);
  };

  // GA4 Init & Page View Tracking
  useEffect(() => {
    initPostHog();
  }, []);

  // add to reoleplay result if any missing chuncks present in indexDb
  useEffect(() => {
    if (typeof window !== "undefined") {
      checkAndRescueIDB().catch(console.error);
    }
  }, []);

  // Successful boot after a chunk-load reload — allow recovery again on a later
  // deploy. Delay clearing so an immediate post-reload chunk failure still hits
  // the one-shot guard instead of looping.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      clearChunkLoadReloadFlag();
    }, 10_000);
    return () => window.clearTimeout(timer);
  }, []);

  // Stale tabs after deploy fail to fetch hashed chunks; one-shot full reload recovers.
  useEffect(() => {
    const handleWindowError = (event: ErrorEvent) => {
      if (!isChunkLoadError(event.error ?? event.message)) return;
      if (recoverFromChunkLoadError()) {
        event.preventDefault();
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (!isChunkLoadError(event.reason)) return;
      if (recoverFromChunkLoadError()) {
        event.preventDefault();
      }
    };

    const handleRouteChangeError = (err: Error) => {
      if (!isChunkLoadError(err)) return;
      recoverFromChunkLoadError();
    };

    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    Router.events.on("routeChangeError", handleRouteChangeError);

    return () => {
      window.removeEventListener("error", handleWindowError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
      Router.events.off("routeChangeError", handleRouteChangeError);
    };
  }, []);

  // SCORM SSO login (set in pages/index.tsx): only the roleplay and its result
  // are reachable; anything else goes back to the launched roleplay. Login
  // and sign-in stay open so a relaunch or an expired session doesn't loop.
  useEffect(() => {
    const allowedPaths = ["/home/roleplay", "/home/roleplay-result", "/signin"];
    const enforceScormPages = (url: string) => {
      const launchUrl = sessionStorage.getItem(SCORM_STORAGE_KEYS.launchUrl);
      if (!launchUrl) return;
      const { pathname, searchParams } = new URL(url, window.location.origin);
      const path = pathname.replace(/\/+$/, "") || "/";
      if (allowedPaths.includes(path)) return;
      if (path === "/" && searchParams.has("token")) return;
      void Router.replace(launchUrl);
    };

    enforceScormPages(window.location.pathname + window.location.search);
    Router.events.on("routeChangeStart", enforceScormPages);
    return () => {
      Router.events.off("routeChangeStart", enforceScormPages);
    };
  }, []);

  // Track page views on route changes
  // in a SPA, we track page change by detecting url changes
  useEffect(() => {
    function handleRouteChange(url: string) {
      trackPageView(url);
    }

    // Track the very first page load
    trackPageView(window.location.pathname + window.location.search);

    Router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      Router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, []);

  // Identify users with their email and org name
  // Renders twice. On the second render it'll identify since on the first render, userDetails would be empty.
  useEffect(() => {
    if (!userDetails?.userId || !userDetails?.orgName) return;

    identifyUser(
      { email: userDetails.userId, userId: userDetails.userId },
      { organizationName: userDetails.orgName }
    );
  }, [userDetails]);

  useEffect(() => {
    const origin = window.location.origin;
    if (origin.includes("stratagileai")) {
      setBranding("stratagile");
      setPageTitle("Stratagile");
    } else if (origin.includes("synergetics-live")) {
      setBranding("synergetics");
      setPageTitle("Synergetics");
    } else {
      setBranding("mple");
      setPageTitle(
        "mple.ai- Transform your sales training to create mple opportunities"
      );
    }
  }, []);

  useEffect(() => {
    const setFirebaseServiceWorker = () => {
      try {
        if (
          typeof window === "undefined" ||
          !("serviceWorker" in navigator) ||
          isIOS() ||
          getAppMeta()
        ) {
          return;
        }

        navigator.serviceWorker
          .register("/firebase-messaging-sw.js")
          .then((registration) => {
            window.__SW_REG = registration;
          })
          .catch((err) => {
            console.error("❌ SW registration failed:", err);
          });
      } catch (error) {
        logger.log("error", error);
      }
    };

    setFirebaseServiceWorker();
  }, []);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !("serviceWorker" in navigator) ||
      isIOS() ||
      getAppMeta()
    ) {
      return;
    }

    let unsubscribe: any;

    const initMessaging = async () => {
      try {
        const supported = await isSupported();
        if (!supported) {
          console.warn("Firebase Messaging is not supported in this browser.");
          return;
        }

        const messaging = getMessaging(firebaseApp);

        unsubscribe = onMessage(messaging, async (payload) => {
          const { title, body } = payload.notification || {};

          if (Notification.permission === "granted") {
            try {
              let registration = window.__SW_REG;

              registration ??= await navigator.serviceWorker.getRegistration();

              if (registration) {
                void registration.showNotification(title || "New Message", {
                  body: body || "",
                });
              }
            } catch (err) {
              console.error("Notification error:", err);
            }
          }
        });
      } catch (error) {
        logger.log("Firebase error:", error);
      }
    };

    void initMessaging();

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") {
      // 1. Disable right click (context menu), except in data-allow-copy content
      const handleContextMenu = (e: MouseEvent) => {
        if (isCopyAllowed(e.target)) {
          return;
        }
        e.preventDefault();
      };

      // 2. Disable selectstart event directly (except in inputs/textareas and
      // data-allow-copy content)
      const handleSelectStart = (e: Event) => {
        const target = getEventElement(e.target);
        if (
          target &&
          (target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            (target as HTMLElement).isContentEditable ||
            isCopyAllowed(target))
        ) {
          return;
        }
        e.preventDefault();
      };

      // 3. iOS (Safari and the app's WKWebView) fires no contextmenu on long-press and
      // ignores selectstart, so only CSS stops the native copy menu there. Same
      // exceptions as above: inputs, editable content and data-allow-copy content.
      const noSelectStyle = document.createElement("style");
      noSelectStyle.textContent = `
        body {
          -webkit-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
        }
        input, textarea, [contenteditable], [contenteditable] *,
        [data-allow-copy], [data-allow-copy] * {
          -webkit-user-select: text;
          user-select: text;
          -webkit-touch-callout: default;
        }
      `;
      document.head.appendChild(noSelectStyle);

      window.addEventListener("contextmenu", handleContextMenu);
      window.addEventListener("selectstart", handleSelectStart);

      return () => {
        window.removeEventListener("contextmenu", handleContextMenu);
        window.removeEventListener("selectstart", handleSelectStart);
        noSelectStyle.remove();
      };
    }
  }, []);

  useEffect(() => {
    const storedUserId: string = localStorage.getItem("email") || "";
    const storedOrgName: string = localStorage.getItem("orgName") || "";

    setUserDetails({
      userId: storedUserId,
      orgName: storedOrgName,
    });

    const rightClickListener = (e: MouseEvent) => {
      if (process.env.NODE_ENV !== "development" && !isCopyAllowed(e.target)) {
        e.preventDefault();
      }
    };

    window.addEventListener("contextmenu", rightClickListener);
    return () => {
      window.removeEventListener("contextmenu", rightClickListener);
    };
  }, []);

  useEffect(() => {
    const updatePwaFlag = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        return;
      }

      // Get app meta to verify it's actually from PWA
      const appMeta = getAppMeta();
      if (!appMeta) {
        return;
      }

      try {
        await protectedAxiosInstance.post(
          "/users/update-user-agent",
          {
            pwa: true,
            version: appMeta.version || null,
            osVersion: appMeta.osVersion || null,
            platform: appMeta.platform || null,
          },
          { timeout: 10000 }
        );

        localStorage.setItem("pwaUpdated", "true");
      } catch (error: any) {
        const status = Number(error?.response?.status || 0);
        if (status !== 401 && status !== 403) {
          console.warn(
            "Failed to update browser PWA flag:",
            status || error?.message || "unknown error"
          );
        }
      }
    };

    // Wait for app to be fully loaded and login to complete
    // Poll for token availability instead of a fixed timeout
    const checkTokenAndRun = () => {
      const token = localStorage.getItem("token");
      if (token) {
        void updatePwaFlag();
      } else {
        // If no token, check again in 1 second
        const timer = setTimeout(checkTokenAndRun, 1000);
        return () => clearTimeout(timer);
      }
    };

    // Start checking
    checkTokenAndRun();
  }, []);

  useEffect(() => {
    if (isSuspiciousEnvironment()) {
      setIsSuspicious(true);
    }
  }, []);

  useEffect(() => {
    const handleStatusChange = () => {
      const isNowOnline = navigator.onLine;
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      if (isNowOnline) {
        setDebouncedOnline(true);
      } else {
        debounceTimeoutRef.current = setTimeout(() => {
          setDebouncedOnline(false);
        }, 3000);
      }
    };

    window.addEventListener("online", handleStatusChange);
    window.addEventListener("offline", handleStatusChange);

    return () => {
      window.removeEventListener("online", handleStatusChange);
      window.removeEventListener("offline", handleStatusChange);
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const splashLogo =
    branding === "stratagile"
      ? "/splash-images/STRATAGILE_LOGO.png"
      : branding === "synergetics"
        ? "/splash-images/SYNERGETICS_LOGO.png"
        : null;

  if (!debouncedOnline) {
    return <OfflineScreen />;
  }

  if (isSuspicious) {
    return <Custom404 />;
  }

  return (
    <ErrorBoundary>
      <Head>
        <title>{pageTitle}</title>
        <meta property="og:title" content={pageTitle} key="title" />
        <meta
          property="og:description"
          content="Transform your sales training to create mple opportunities. Screen, Onboard and Train With Artificial Intelligence. AI-Powered Recruitment, Automated Onboarding, Product Training and Coaching, AI Role Play for Sales Training."
          key="description"
        />
        <meta property="og:site_name" content={pageTitle} key="site_name" />
        <meta property="og:image" content="/images/logo.png" key="image" />
        <link rel="manifest" href="/manifest.json" />
        <meta name="mobile-web-app-capable" content="yes" />
        {branding !== null && (
          <>
            <link
              rel="apple-touch-icon"
              href={splashLogo ? splashLogo : "/splash-images/apple-icon.png"}
            ></link>

            {/* Updated favicon links */}

            <link
              rel="icon"
              type="image/png"
              href={splashLogo ? splashLogo : "/splash-images/icon1.png"}
              sizes="96x96"
            />
            <link
              rel="shortcut icon"
              href={splashLogo ? splashLogo : "/splash-images/icon1.png"}
            />

            {/* <!-- iPhone SE (1st gen) --> */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-640x1136.png"
              }
              media="(device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2)"
            />

            {/* iPhone 6 / 7 / 8 / SE (2nd & 3rd Gen) */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-750x1334.png"
              }
              media="(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2)"
            />

            {/* iPhone 6+/7+/8+ */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-1242x2208.png"
              }
              media="(device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3)"
            />

            {/* iPhone X / XS / 11 Pro */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-1125x2436.png"
              }
              media="(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3)"
            />

            {/* iPhone XR / 11 */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-828x1792.png"
              }
              media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2)"
            />

            {/* iPhone XS Max / 11 Pro Max */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-1242x2688.png"
              }
              media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3)"
            />

            {/* iPhone 12 Mini / 13 Mini */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-1080x2340.png"
              }
              media="(device-width: 360px) and (device-height: 780px) and (-webkit-device-pixel-ratio: 3)"
            />

            {/* iPhone 12 / 12 Pro / 13 / 13 Pro / 14 */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-1170x2532.png"
              }
              media="(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3)"
            />

            {/* iPhone 12 Pro Max / 13 Pro Max / 14 Plus */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-1284x2778.png"
              }
              media="(device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3)"
            />

            {/* <!-- iPhone 14 Pro / 15 / 15 Pro --> */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-1179x2556.png"
              }
              media="(device-width: 393px) and (device-height: 852px) and (-webkit-device-pixel-ratio: 3)"
            />

            {/* iPhone 14 Pro / 15 / 15 Pro / 16 / 17 base & Pro */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-1206x2622.png"
              }
              media="((device-width: 393px) and (device-height: 852px) or (device-width: 390px) and (device-height: 844px)) and (-webkit-device-pixel-ratio: 3)"
            />

            {/* iPhone 14 Pro Max / 15 Pro Max / 16 Plus / 17 Plus */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-1290x2796.png"
              }
              media="(device-width: 430px) and (device-height: 932px) and (-webkit-device-pixel-ratio: 3)"
            />

            {/* iPhone 16 Plus */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-1366x2933.png"
              }
              media="(device-width: 430px) and (device-height: 945px) and (-webkit-device-pixel-ratio: 3)"
            />

            {/* iPhone 16 Pro Max / 17 Pro Max */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-1320x2868.png"
              }
              media="(device-width: 440px) and (device-height: 956px) and (-webkit-device-pixel-ratio: 3)"
            />

            {/* ==== iPads ==== */}

            {/* iPad Mini / iPad 9.7" */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-1536x2048.png"
              }
              media="(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2)"
            />

            {/* iPad Air (10.5") */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-1668x2224.png"
              }
              media="(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2)"
            />

            {/* iPad Pro (11") */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-1668x2388.png"
              }
              media="(device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2)"
            />

            {/* iPad Pro (12.9") */}
            <link
              rel="apple-touch-startup-image"
              href={
                splashLogo
                  ? splashLogo
                  : "/splash-images/apple-splash-2048x2732.png"
              }
              media="(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2)"
            />

            {/* Fallback splash for any unmatched device */}
            <link
              rel="apple-touch-startup-image"
              href={splashLogo ? splashLogo : "/splash-images/apple-icon.png"}
            />
            <meta
              name="apple-mobile-web-app-status-bar-style"
              content="black"
            />
            <meta name="apple-mobile-web-app-title" content="mple.ai" />
            <link
              rel="icon"
              type="image/png"
              href={splashLogo ? splashLogo : "/splash-images/icon1.png"}
              sizes="96x96"
            />
            <link
              rel="icon"
              type="image/svg+xml"
              href={
                branding === null
                  ? undefined
                  : splashLogo
                    ? splashLogo
                    : "/splash-images/icon1.png"
              }
            />
            <link
              rel="shortcut icon"
              href={splashLogo ? splashLogo : "/splash-images/favicon.ico"}
            />
          </>
        )}
        {/* <meta name="theme-color" content="#fff" /> */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, viewport-fit=cover, interactive-widget=resizes-content"
        />
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      {impersonationUserName ? (
        <div className="sticky top-0 z-[1000] flex items-center justify-between gap-3 bg-amber-100 px-4 py-2 text-sm text-amber-950">
          <span className="min-w-0 flex-1">
            <span
              className={`block truncate sm:hidden ${
                isImpersonationBannerExpanded ? "hidden" : ""
              }`}
            >
              You are viewing as {impersonationUserName}.
            </span>
            <span
              className={`${
                isImpersonationBannerExpanded ? "block" : "hidden"
              } sm:block`}
            >
              You are viewing as {impersonationUserName}. Activities during this
              session will be captured as this user.
            </span>
          </span>
          <button
            type="button"
            className="shrink-0 sm:hidden"
            aria-label={
              isImpersonationBannerExpanded
                ? "Collapse impersonation banner"
                : "Expand impersonation banner"
            }
            aria-expanded={isImpersonationBannerExpanded}
            onClick={() =>
              setIsImpersonationBannerExpanded((expanded) => !expanded)
            }
          >
            {isImpersonationBannerExpanded ? (
              <FiChevronUp size={18} />
            ) : (
              <FiChevronDown size={18} />
            )}
          </button>
          <button
            type="button"
            className="shrink-0"
            onClick={exitImpersonation}
            aria-label="Exit impersonation"
            title="Exit impersonation"
          >
            <FiLogOut size={18} />
          </button>
        </div>
      ) : null}

      <AvatarModelContextProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <AuthStoreProvider>
              <WebViewBridgeProvider>
                <PostHogController />
                <Suspense fallback={<Loader />}>
                  <Component {...pageProps} />
                </Suspense>
                <ProfileLandmarkGate />
                <NotificationPermissionModal />
                <OnboardingTour />
              </WebViewBridgeProvider>
            </AuthStoreProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </AvatarModelContextProvider>

      <UpdateAppModal />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnHover
        theme="light"
      />
    </ErrorBoundary>
  );
}

export default App;
